import { type MtgColor } from '@ugin/shared';
import { motion } from 'motion/react';
import { useState } from 'react';

import { useCardLookup } from './useCardLookup';

import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';


export interface CardPreviewProps {
  name: string;
  trigger?: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

export function CardPreview({ name, trigger, side = 'top', className }: CardPreviewProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = useCardLookup(name, open);

  const content = (() => {
    if (isLoading || !data) {
      return (
        <div className="w-72 space-y-2 p-3" aria-busy="true">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="w-72 p-3 text-sm text-ugin-danger" role="alert">
          Could not find “{name}”.
        </div>
      );
    }
    return <CardFace card={data} />;
  })();

  const inner = trigger ?? (
    <span className="cursor-help underline decoration-dotted decoration-ugin-accent/60 underline-offset-2 hover:text-ugin-text">
      {name}
    </span>
  );

  return (
    <TooltipProvider delayDuration={180}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent
          side={side}
          align="center"
          className={cn('p-0 bg-transparent border-none shadow-none', className)}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface CardFaceData {
  name: string;
  manaCost?: string | null;
  typeLine?: string | null;
  oracleText?: string | null;
  rarity?: string | null;
  colors?: MtgColor[] | null;
  imageUris?: { art_crop?: string; small?: string; normal?: string } | null;
}

function CardFace({ card }: { card: CardFaceData }) {
  const colorClass = colorToFrameGradient(card.colors ?? []);
  const art = card.imageUris?.art_crop ?? card.imageUris?.normal ?? card.imageUris?.small;
  return (
    <motion.article
      initial={{ opacity: 0, scale: 0.96, y: 4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'relative w-72 overflow-hidden rounded-2xl border border-ugin-border bg-ugin-surface-elevated shadow-2xl',
        colorClass,
      )}
      style={{ backgroundImage: 'var(--frame-bg)' }}
      aria-label={`Card preview: ${card.name}`}
    >
      <header className="flex items-center justify-between gap-2 bg-ugin-bg-deep/60 px-3 py-2 backdrop-blur-sm">
        <h3 className="truncate font-display text-base text-ugin-text" title={card.name}>
          {card.name}
        </h3>
        {card.manaCost ? (
          <ManaCost cost={card.manaCost} />
        ) : null}
      </header>

      <div className="relative aspect-[5/3] bg-ugin-bg overflow-hidden">
        {art ? (
          <img
            src={art}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-ugin-text-faint">
            <span className="text-xs uppercase tracking-widest">no art</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ugin-bg-deep to-transparent" />
      </div>

      <div className="space-y-2 px-3 py-2.5">
        {card.typeLine ? (
          <p className="text-xs uppercase tracking-wider text-ugin-text-muted">{card.typeLine}</p>
        ) : null}
        {card.oracleText ? (
          <p className="text-[13px] leading-snug text-ugin-text whitespace-pre-line">
            {card.oracleText}
          </p>
        ) : null}
        {card.rarity ? (
          <p className="text-[10px] uppercase tracking-widest text-ugin-text-faint">
            {card.rarity}
          </p>
        ) : null}
      </div>
    </motion.article>
  );
}

function ManaCost({ cost }: { cost: string }) {
  const symbols = cost.match(/\{[^}]+\}/g) ?? [];
  if (symbols.length === 0) return null;
  return (
    <div className="flex items-center gap-0.5" aria-label={`Cost ${cost}`}>
      {symbols.map((sym, i) => (
        <ManaPip key={i} symbol={sym} />
      ))}
    </div>
  );
}

function ManaPip({ symbol }: { symbol: string }) {
  const inner = symbol.slice(1, -1).toUpperCase();
  const single = /^[WUBRGC]$/.test(inner);
  const color =
    inner === 'W' ? 'bg-mtg-w text-ugin-bg-deep' :
    inner === 'U' ? 'bg-mtg-u text-white' :
    inner === 'B' ? 'bg-mtg-b text-ugin-text' :
    inner === 'R' ? 'bg-mtg-r text-white' :
    inner === 'G' ? 'bg-mtg-g text-white' :
    inner === 'C' ? 'bg-mtg-c text-ugin-bg-deep' :
    'bg-ugin-surface-overlay text-ugin-text';
  return (
    <span
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ring-1 ring-black/30',
        color,
      )}
    >
      {single ? inner : inner.slice(0, 2)}
    </span>
  );
}

function colorToFrameGradient(colors: MtgColor[]): string {
  if (colors.length === 0)
    return '[--frame-bg:linear-gradient(135deg,rgba(160,150,120,0.25),rgba(20,20,30,0.4))]';
  const map: Record<MtgColor, string> = {
    W: 'rgba(248,236,200,0.35)',
    U: 'rgba(60,120,220,0.35)',
    B: 'rgba(40,30,30,0.5)',
    R: 'rgba(220,80,60,0.35)',
    G: 'rgba(80,180,100,0.35)',
    C: 'rgba(180,170,140,0.3)',
  };
  const first = colors[0];
  if (first === undefined) return '[--frame-bg:rgba(20,20,30,0.5)]';
  if (colors.length === 1) {
    return `[--frame-bg:linear-gradient(135deg,${map[first]},rgba(20,20,30,0.5))]`;
  }
  const stops = colors.map((c) => map[c]).filter(Boolean);
  return `[--frame-bg:linear-gradient(135deg,${stops.join(',')})]`;
}
